import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Copy, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Activate({ isRenewal, defaultPlanId }) {
  const { state, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(defaultPlanId || "solo");
  const [payments, setPayments] = useState([]);
  const [utr, setUtr] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = () => api.myPayments().then(setPayments);
  useEffect(() => {
    api.paymentInfo().then(setInfo);
    api.getPlans().then((p) => { setPlans(p); if (!defaultPlanId && p[0]) setSelectedPlanId((cur) => cur || p[0].id); });
    reload();
  }, [defaultPlanId]);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const amount = selectedPlan?.price ?? info?.amount ?? 499;
  const pending = payments.find((p) => p.status === "pending");
  const upiUri = info
    ? `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=${encodeURIComponent(info.payee_name)}&am=${amount}&cu=INR`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      let screenshotPath = null;
      if (screenshot) {
        setUploading(true);
        const up = await api.uploadScreenshot(screenshot);
        screenshotPath = up.path;
        setUploading(false);
      }
      await api.submitPayment({ utr: utr.trim(), payment_date: date, notes, screenshot: screenshotPath, plan_id: selectedPlanId });
      toast.success("Payment submitted for verification");
      setUtr(""); setNotes(""); setScreenshot(null);
      await reload();
      await refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); setUploading(false); }
    setLoading(false);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8" data-testid="activate-page">
      {state === "EXPIRED" && (
        <div className="mb-6 rounded-xl border border-destructive/40 bg-destructive/5 p-4 flex items-center gap-3" data-testid="expired-banner">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm">Your subscription has expired. Renew to reactivate your QR — everything else is preserved.</p>
        </div>
      )}

      {!pending && (
        <div className="mb-6" data-testid="plan-picker">
          <p className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground text-center mb-4">Choose your plan</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {plans.map((p) => (
              <button key={p.id} type="button" onClick={() => setSelectedPlanId(p.id)} data-testid={`plan-${p.id}`}
                className={`text-left rounded-2xl border-2 p-4 transition-colors ${selectedPlanId === p.id ? "border-accent bg-accent/5" : "border-border hover:border-muted-foreground/40"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold tracking-tight">{p.name}</span>
                  {selectedPlanId === p.id && <CheckCircle2 className="h-4 w-4 text-accent" />}
                </div>
                <div className="mt-1 font-display text-2xl font-extrabold tracking-tighter">₹{p.price}<span className="text-xs text-muted-foreground font-medium">/yr</span></div>
                <p className="mt-1 text-xs text-accent font-medium">{p.qr_limit} QR{p.qr_limit > 1 ? "s" : ""}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{p.tagline}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border-2 border-accent bg-card p-7 text-center glow-green">
        <p className="font-display text-sm font-bold uppercase tracking-widest text-accent">DynaQR {selectedPlan?.name || "Solo"}</p>
        <div className="mt-2 font-display text-4xl font-extrabold tracking-tighter">₹{amount}<span className="text-base text-muted-foreground font-medium"> / year</span></div>
        <p className="mt-1 text-sm text-muted-foreground">{isRenewal ? "Renew your subscription" : "Activate your account"} · {selectedPlan?.qr_limit || 1} QR{(selectedPlan?.qr_limit || 1) > 1 ? "s" : ""}</p>

        {pending ? (
          <div className="mt-6 rounded-xl border border-border bg-secondary/50 p-5" data-testid="payment-pending-box">
            <Clock className="h-8 w-8 mx-auto text-accent mb-2" />
            <p className="font-display font-bold text-lg">Payment under verification</p>
            <p className="mt-1 text-sm text-muted-foreground">UTR: {pending.utr}</p>
            <p className="mt-2 text-xs text-muted-foreground">Your account will be activated once the admin approves your payment.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-col items-center">
              <div className="rounded-2xl bg-white p-4 shadow-lg">
                {upiUri && <QRCodeCanvas value={upiUri} size={180} level="M" />}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-full border border-border px-4 py-2">
                <span className="text-sm font-medium" data-testid="upi-id">{info?.upi_id}</span>
                <button onClick={() => { navigator.clipboard.writeText(info?.upi_id || ""); toast.success("UPI ID copied"); }}>
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Scan with any UPI app (GPay, PhonePe, Paytm) and pay ₹{amount}</p>
            </div>

            <form onSubmit={submit} className="mt-6 text-left space-y-4">
              <p className="font-display font-bold text-center">I have completed payment</p>
              {error && <p className="text-sm text-destructive text-center">{error}</p>}
              <div className="space-y-2">
                <Label>UTR / Transaction ID</Label>
                <Input data-testid="utr-input" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="12-digit UTR from your UPI app" required />
              </div>
              <div className="space-y-2">
                <Label>Payment date</Label>
                <Input type="date" data-testid="payment-date-input" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea data-testid="payment-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Payment screenshot (optional)</Label>
                <Input type="file" accept="image/*" data-testid="payment-screenshot-input"
                  onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
                {screenshot && <p className="text-xs text-muted-foreground">Selected: {screenshot.name}</p>}
              </div>
              <Button type="submit" disabled={loading || uploading} className="w-full rounded-full" data-testid="submit-payment-btn">
                {(loading || uploading) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {uploading ? "Uploading..." : `Submit payment — ₹${amount}`}
              </Button>
            </form>
          </>
        )}
      </div>

      {payments.length > 0 && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="payment-history">
          <p className="font-display font-bold mb-3">Payment history</p>
          <ul className="space-y-2">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm">
                <span>{p.payment_date} · UTR {p.utr}</span>
                <Badge className={
                  p.status === "approved" ? "bg-accent text-accent-foreground" :
                  p.status === "rejected" ? "bg-destructive text-destructive-foreground" : "bg-secondary text-secondary-foreground"
                }>
                  {p.status === "approved" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                  {p.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
